import React, { useEffect, useMemo, useRef, useState, type Key } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  ConfigProvider,
  Empty,
  Flex,
  Input,
  Menu,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  theme,
  type TableColumnType,
  type TableProps,
} from 'antd';
import {
  ArrowRightOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { type SortOrder } from 'antd/es/table/interface';
import { debounce } from 'lodash';

import { CustomTooltip } from '~components/Tooltip';
import StatusDropdownFilter, { type StatusCodeFilter } from '../../../../components/dropdownFilters/StatusDropdownFilter';
import { AiInsightsEmptyState, AiInsightsSectionHeading, useAiCrawlerLineup } from '../../shared';
import {
  useGetDiscoverabilityPagesCountsQuery,
  useGetDiscoverabilityPagesQuery,
  type PageToReview,
  type PagesToReviewCounts,
  type PagesToReviewSortColumn,
  type PagesToReviewSortDirection,
  type PagesToReviewTab,
  type PagesToReviewUrlSearchCondition,
} from '../api/discoverabilityPagesApi';
import { resolveDiscoverabilityPagesScope } from '../api/discoverabilityPagesScope';
import { buildCdnAnalyticsLink } from '../cdnAnalyticsDeepLink';
import { statusCodeTagColor } from '../pagesToReviewStatusCode';
import { useDiscoverabilityEntitlement } from '../hooks/useDiscoverabilityEntitlement';
import PagesToReviewExportModal from './PagesToReviewExportModal';
import { useDiscoverabilitySearchParams } from '../hooks/useDiscoverabilitySearchParams';
import { useScanScopeParams } from '../hooks/useScanScopeParams';
import {
  DEFAULT_URL_SEARCH_CONDITION,
  PAGES_TO_REVIEW_DEFAULT_SORT_BY,
  PAGES_TO_REVIEW_DEFAULT_SORT_DIRECTION,
  URL_SEARCH_MIN_LENGTH,
  usePagesToReviewTableParams,
} from '../hooks/usePagesToReviewTableParams';
import useTrackDiscoverabilityEvent from '../hooks/useTrackDiscoverabilityEvent';
import useQueryStringPagination from '../../../../utils/useQueryStringPagination';

export const PAGE_SIZE = 25;
export const TROUBLESHOOTING_GUIDE_URL = 'https://docs.prerender.io/docs/status-codes';
export const URL_SEARCH_DEBOUNCE_MS = 300;
export const URL_SEARCH_PLACEHOLDER = 'Enter at least 3 characters';
const SORTABLE_HEADER_TOOLTIP = 'Click to sort';

// Parses an untrusted `page`/`pageSize` URL param. Anything that can't address a
// real page — negative, zero, or non-numeric — is "junk" and returns undefined.
function parsePositivePageParam(raw: string | null): number | undefined {
  if (raw === null) return undefined;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

const URL_SEARCH_CONDITION_OPTIONS: {
  value: PagesToReviewUrlSearchCondition;
  label: string;
}[] = [
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does not contain' },
  { value: 'equals', label: 'Is equal to' },
  { value: 'not_equals', label: 'Is not equal to' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
];

const SORTER_FIELD_TO_SORT_COLUMN: Record<string, PagesToReviewSortColumn> = {
  url: 'url',
  aiCrawlerVisits: 'ai_crawler_visits',
  lastStatusCode: 'status',
};

const SORT_DIRECTION_TO_ANTD: Record<PagesToReviewSortDirection, SortOrder> = {
  asc: 'ascend',
  desc: 'descend',
};

type EmptyStateCopy = {
  headline: string;
  body: string;
};

type TabDefinition = {
  key: PagesToReviewTab;
  label: string;
  tooltip: string;
  // Selects this tab's issue count from the scope-only counts response. The
  // All Pages tab returns undefined so it never renders a badge.
  selectCount: (counts: PagesToReviewCounts) => number | undefined;
  // Shown when the tab has no rows and no filters are active (unless the site
  // is still waiting for its first crawler request).
  emptyState: EmptyStateCopy;
};

const HEALTHY_EMPTY_STATE: EmptyStateCopy = {
  headline: 'Everything looks good',
  body: "We didn't find anything that needs attention in this tab.",
};

// All Pages is an inventory of every crawled page, not an issue scan, so an
// empty tab means no crawls in the range — not "nothing needs attention".
const NO_CRAWLS_EMPTY_STATE: EmptyStateCopy = {
  headline: 'No pages crawled',
  body: "AI crawlers didn't request any pages in the selected period.",
};

const WAITING_FOR_CRAWLERS_EMPTY_STATE: EmptyStateCopy = {
  headline: 'Waiting for AI Crawler requests',
  body: 'Once AI Crawlers visit your site, your data will appear here.',
};

const TABS: TabDefinition[] = [
  {
    key: 'access_blocked',
    label: 'Access Blocked',
    tooltip: 'Pages where AI crawlers were blocked from accessing content.',
    selectCount: (counts) => counts.accessBlocked,
    emptyState: HEALTHY_EMPTY_STATE,
  },
  {
    key: 'access_limited',
    label: 'Access Limited',
    tooltip: 'Pages where AI crawlers hit errors or redirects that limit access.',
    selectCount: (counts) => counts.accessLimited,
    emptyState: HEALTHY_EMPTY_STATE,
  },
  {
    key: 'all_pages',
    label: 'All Pages',
    tooltip: 'Every page AI crawlers requested in the selected period.',
    selectCount: () => undefined,
    emptyState: NO_CRAWLS_EMPTY_STATE,
  },
];

type PagesToReviewSectionProps = {
  /** True while the site has never received AI crawler traffic (the KPI response's `hasEverHadCrawlerActivity` is false). */
  waitingForCrawlerRequests?: boolean;
  /** False while the parent view is still settling (e.g. the KPIs query is loading), so the tab impression is held back. */
  impressionsReady?: boolean;
};

export default function PagesToReviewSection({
  waitingForCrawlerRequests = false,
  impressionsReady = true,
}: PagesToReviewSectionProps) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const trackEvent = useTrackDiscoverabilityEvent();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const entitlement = useDiscoverabilityEntitlement();
  const isNoIntegration = entitlement.kind === 'no-integration';
  const [searchParams, setSearchParams] = useSearchParams();
  const { updatePagination } = useQueryStringPagination(PAGE_SIZE);
  const { tableParams, setUrlSearch, applyTableChange, clearFilters } = usePagesToReviewTableParams();

  // Untrusted `page`/`pageSize` URL params (stale links, hand-edits). Junk
  // params fall back to the first page / default size for the fetch; the effect
  // below then rewrites the URL to match where the user actually landed.
  const rawPage = searchParams.get('page');
  const rawPageSize = searchParams.get('pageSize');
  const pageParamIsJunk = rawPage !== null && parsePositivePageParam(rawPage) === undefined;
  const pageSizeParamIsJunk = rawPageSize !== null && parsePositivePageParam(rawPageSize) === undefined;
  const pageSize = parsePositivePageParam(rawPageSize) ?? PAGE_SIZE;
  const requestedPage = parsePositivePageParam(rawPage) ?? 1;

  // Falls back to the first tab for a missing or unknown `tab` URL param.
  const activeTabDefinition = TABS.find((tab) => tab.key === searchParams.get('tab')) ?? TABS[0];
  const activeTab = activeTabDefinition.key;
  const primaryLinkStyle = { color: token.colorPrimary };

  const { dateFrom, dateTo, crawlers, domain } = useDiscoverabilitySearchParams();
  const lineup = useAiCrawlerLineup();

  // The scan-scope params pin the table and badges to the exact population the
  // daily scan counted; they carry only when the user arrived via an Inspect
  // pages CTA (or a shared/reloaded scan URL). Absent them, the calendar picker
  // days resolve to a local-timezone datetime window instead.
  const { dateTimeFrom, dateTimeTo, domains } = useScanScopeParams();

  // The Pages-to-Review endpoints take the absolute datetime form exclusively:
  // either the scan window, or the picker days resolved to local instants (with
  // a single-domain filter carried as a one-entry `domains` list).
  const pagesScope = resolveDiscoverabilityPagesScope({
    dateFrom,
    dateTo,
    crawlers,
    domain,
    scanDateTimeFrom: dateTimeFrom,
    scanDateTimeTo: dateTimeTo,
    scanDomains: domains,
  });

  const { data, isFetching, refetch } = useGetDiscoverabilityPagesQuery({
    ...pagesScope,
    tab: activeTab,
    page: requestedPage,
    pageSize,
    sortBy: tableParams.sortBy,
    sortDirection: tableParams.sortDirection,
    urlSearch: tableParams.urlSearch,
    urlSearchCondition: tableParams.urlSearchCondition,
    statusEquals: tableParams.statusEquals,
    statusFrom: tableParams.statusFrom,
    statusTo: tableParams.statusTo,
  });

  const { data: counts } = useGetDiscoverabilityPagesCountsQuery(pagesScope);

  const [searchText, setSearchText] = useState(tableParams.urlSearch ?? '');
  const [searchCondition, setSearchCondition] = useState(
    tableParams.urlSearchCondition ?? DEFAULT_URL_SEARCH_CONDITION
  );

  // Remembers our own URL writes so the sync effect skips them — otherwise
  // backspacing below URL_SEARCH_MIN_LENGTH would wipe the in-progress text.
  const committedUrlSearch = useRef(tableParams.urlSearch);

  // Keeps the input in sync when the URL params change from elsewhere
  // (Clear filters, browser navigation), but never clobbers in-progress typing.
  useEffect(() => {
    if (tableParams.urlSearch === committedUrlSearch.current) return;
    committedUrlSearch.current = tableParams.urlSearch;
    setSearchText(tableParams.urlSearch ?? '');
  }, [tableParams.urlSearch]);

  // Pulls a condition in from the URL (deep links, browser navigation) but
  // never resets on absence: the URL drops the condition whenever the search
  // text clears, while the selector keeps the user's pick for the next search.
  useEffect(() => {
    if (tableParams.urlSearchCondition !== undefined) {
      setSearchCondition(tableParams.urlSearchCondition);
    }
  }, [tableParams.urlSearchCondition]);

  const applyUrlSearch = useMemo(
    () =>
      debounce((rawText: string, condition: PagesToReviewUrlSearchCondition) => {
        const urlSearchText = rawText.trim();
        if (urlSearchText.length >= URL_SEARCH_MIN_LENGTH) {
          committedUrlSearch.current = urlSearchText;
          setUrlSearch(urlSearchText, condition);
          trackEvent('Discoverability: Pages Table URL Searched', {
            tab: activeTab,
            condition,
          });
        } else {
          committedUrlSearch.current = undefined;
          setUrlSearch(undefined);
        }
      }, URL_SEARCH_DEBOUNCE_MS),
    [setUrlSearch, trackEvent, activeTab]
  );

  useEffect(() => () => applyUrlSearch.cancel(), [applyUrlSearch]);

  const handleTabChange = (tab: PagesToReviewTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    applyUrlSearch(event.target.value, searchCondition);
  };

  const handleConditionChange = (condition: PagesToReviewUrlSearchCondition) => {
    setSearchCondition(condition);
    applyUrlSearch(searchText, condition);
  };

  const handleRefresh = () => {
    trackEvent('Discoverability: Pages Table Refresh Clicked', { tab: activeTab });
    refetch();
  };

  const handleClearFilters = () => {
    trackEvent('Discoverability: Pages Table Clear Filters Clicked', {
      tab: activeTab,
    });
    // A search typed within the debounce window is not in the URL yet, so
    // clearFilters() alone would let it commit right after the clear.
    applyUrlSearch.cancel();
    setSearchText('');
    clearFilters();
    setSearchCondition(DEFAULT_URL_SEARCH_CONDITION);
  };

  const handleTableChange: TableProps<PageToReview>['onChange'] = (_pagination, filters, rawSorter, extra) => {
    // Pagination is handled by the pagination prop's own onChange.
    if (extra.action === 'paginate') return;

    const sorter = Array.isArray(rawSorter) ? rawSorter[0] : rawSorter;
    const sortBy = sorter.order ? SORTER_FIELD_TO_SORT_COLUMN[String(sorter.field)] : undefined;
    const sortDirection: PagesToReviewSortDirection | undefined =
      sortBy === undefined ? undefined : sorter.order === 'ascend' ? 'asc' : 'desc';
    const statusFilter = (filters.lastStatusCode as StatusCodeFilter[] | null)?.[0];

    applyTableChange({
      sortBy,
      sortDirection,
      statusEquals: statusFilter?.statusCodeEq,
      statusFrom: statusFilter?.statusCodeLow,
      statusTo: statusFilter?.statusCodeHigh,
    });

    if (extra.action === 'sort') {
      trackEvent('Discoverability: Pages Table Sort Changed', {
        tab: activeTab,
        sortBy: sortBy ?? PAGES_TO_REVIEW_DEFAULT_SORT_BY,
        sortDirection: sortDirection ?? PAGES_TO_REVIEW_DEFAULT_SORT_DIRECTION,
      });
    }
  };

  // The last page that actually holds rows for the current result set.
  const lastPage = data !== undefined && data.totalCount > 0 ? Math.ceil(data.totalCount / pageSize) : 1;
  // A valid page number past the end (e.g. a bookmarked link after the data set
  // shrank) — the nearest real page is the last one, not the first.
  const pageExceedsData =
    !pageParamIsJunk && !isFetching && data !== undefined && data.totalCount > 0 && requestedPage > lastPage;
  // While correcting, the table would otherwise flash the misleading "Everything
  // looks good" empty state even though the badge shows issues exist.
  const isCorrectingPagination = pageParamIsJunk || pageSizeParamIsJunk || pageExceedsData;

  useEffect(() => {
    if (pageParamIsJunk || pageSizeParamIsJunk) {
      // Junk params can't address a real page — drop the user on the first page.
      updatePagination(1, pageSize);
    } else if (pageExceedsData) {
      // Too-large but real number — drop the user on the nearest real page.
      updatePagination(lastPage, pageSize);
    }
  }, [pageParamIsJunk, pageSizeParamIsJunk, pageExceedsData, lastPage, pageSize, updatePagination]);

  const sortOrderFor = (column: PagesToReviewSortColumn): SortOrder =>
    tableParams.sortBy === column ? SORT_DIRECTION_TO_ANTD[tableParams.sortDirection] : null;

  const statusFilterActive =
    tableParams.statusEquals !== undefined ||
    tableParams.statusFrom !== undefined ||
    tableParams.statusTo !== undefined;

  const emptyState = waitingForCrawlerRequests
    ? WAITING_FOR_CRAWLERS_EMPTY_STATE
    : activeTabDefinition.emptyState;

  // With no rows, no active filters and no fetch in flight (the healthy and
  // waiting-for-data states), the design replaces the toolbar and the bordered
  // table with a plain empty state — searching or filtering can't surface
  // anything. The table stays while fetching to avoid flashing on load, and
  // when filters are active the toolbar is the recovery path.
  const showEmptyStateOnly =
    !tableParams.hasActiveFilters && !isFetching && data !== undefined && data.totalCount === 0;

  // Fire one impression per genuine tab impression: the section's tabs are
  // shown (after the fetch settles) and the view is not behind the
  // no-integration blur overlay. Empty-state vs populated does not matter —
  // the empty state still renders the tabs, so it is a real impression. The
  // guard tracks the last tab that fired so a refetch or re-render does not
  // re-fire, while switching tabs does. Also held back until the parent
  // signals the view has settled (impressionsReady), so a transient mount
  // before the KPIs resolve does not fire a spurious impression.
  const trackedTabRef = useRef<PagesToReviewTab | undefined>(undefined);
  useEffect(() => {
    if (isNoIntegration || isFetching || !impressionsReady) return;
    if (trackedTabRef.current === activeTab) return;
    trackedTabRef.current = activeTab;
    trackEvent('Discoverability: Pages To Review Tab Viewed', { tab: activeTab });
  }, [activeTab, isNoIntegration, isFetching, impressionsReady, trackEvent]);

  const columns: TableColumnType<PageToReview>[] = [
    {
      title: (
        <CustomTooltip placement="top" message={SORTABLE_HEADER_TOOLTIP}>
          Page URL
        </CustomTooltip>
      ),
      dataIndex: 'url',
      key: 'url',
      sorter: true,
      sortOrder: sortOrderFor('url'),
      render: (url: string) => (
        <Typography.Link href={url} target="_blank" rel="noreferrer" style={primaryLinkStyle}>
          {url}
        </Typography.Link>
      ),
    },
    {
      title: (
        <CustomTooltip placement="top" message={SORTABLE_HEADER_TOOLTIP}>
          Crawl Requests
        </CustomTooltip>
      ),
      dataIndex: 'aiCrawlerVisits',
      key: 'aiCrawlerVisits',
      align: 'right',
      width: 180,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: sortOrderFor('ai_crawler_visits'),
      render: (visits: number) => visits.toLocaleString(),
    },
    {
      title: (
        <CustomTooltip placement="top" message={SORTABLE_HEADER_TOOLTIP}>
          Status
        </CustomTooltip>
      ),
      dataIndex: 'lastStatusCode',
      key: 'lastStatusCode',
      width: 120,
      sorter: true,
      sortOrder: sortOrderFor('status'),
      filteredValue: statusFilterActive
        ? [
            {
              statusCodeEq: tableParams.statusEquals,
              statusCodeLow: tableParams.statusFrom,
              statusCodeHigh: tableParams.statusTo,
              // AntD expects primitive filter keys; the status filter needs a
              // richer structure (same approach as CDNAnalyticsTable).
            } as unknown as Key,
          ]
        : null,
      filterDropdown: ({ confirm, setSelectedKeys, selectedKeys, clearFilters: clearColumnFilters }) => (
        <StatusDropdownFilter
          confirm={confirm}
          selectedKeys={selectedKeys as StatusCodeFilter[]}
          setSelectedKeys={setSelectedKeys as (keys: StatusCodeFilter[]) => void}
          clearFilters={clearColumnFilters}
        />
      ),
      filterDropdownProps: { destroyOnHidden: true },
      render: (statusCode: number) => <Tag color={statusCodeTagColor(statusCode)}>{statusCode}</Tag>,
    },
    {
      title: 'CDN Analytics',
      key: 'cdnAnalytics',
      width: 140,
      align: 'center',
      render: (_: unknown, record: PageToReview) => {
        const link = buildCdnAnalyticsLink(lineup, {
          url: record.url,
          dateFrom,
          dateTo,
          crawlers,
          domain,
        });
        return (
          <Typography.Link
            href={link}
            aria-label={`View ${record.url} in CDN Analytics`}
            style={primaryLinkStyle}
            onClick={(event) => {
              event.preventDefault();
              trackEvent('Discoverability: CDN Analytics Link Clicked', { tab: activeTab });
              navigate(link);
            }}
          >
            <ArrowRightOutlined />
          </Typography.Link>
        );
      },
    },
  ];

  return (
    <Flex vertical gap={token.marginXS}>
      <AiInsightsSectionHeading
        title="Pages to Review"
        description="Inspect pages crawled by AI Crawlers, grouped by access issues and optimization opportunities."
      />

      <Card size="small">
        <ConfigProvider theme={{ components: { Menu: { itemPaddingInline: token.paddingXS } } }}>
          <Menu
            mode="horizontal"
            selectedKeys={[activeTab]}
            onClick={({ key }) => handleTabChange(key as PagesToReviewTab)}
            items={TABS.map((tab) => {
              const count = counts ? tab.selectCount(counts) : undefined;
              return {
                key: tab.key,
                label: (
                  <CustomTooltip placement="top" message={tab.tooltip}>
                    <Flex gap={token.marginXXS} align="center" component="span">
                      {tab.label}
                      {typeof count === 'number' && (
                        <Tag
                          style={{
                            margin: 0,
                            fontSize: token.fontSizeSM,
                            // Figma: control-padding-horizontal-sm (8px) — the
                            // wider horizontal padding gives the pill its width
                            // (paddingXXS collapsed it to a circle).
                            paddingInline: token.paddingXS,
                            backgroundColor: token.colorFillAlter,
                            borderColor: token.colorBorderSecondary,
                            // FIXME: no exact token — Figma's pill radius is
                            // 16px; AntD's largest radius token is 8px.
                            borderRadius: 16,
                          }}
                        >
                          {count}
                        </Tag>
                      )}
                    </Flex>
                  </CustomTooltip>
                ),
              };
            })}
          />
        </ConfigProvider>

        {showEmptyStateOnly ? (
          <div style={{ padding: token.paddingXL }}>
            <AiInsightsEmptyState headline={emptyState.headline} body={emptyState.body} />
          </div>
        ) : (
          <>
            <Flex justify="space-between" align="center" wrap gap="small" style={{ paddingBlock: token.paddingSM }}>
              <Flex gap="small" wrap align="center">
                <Space.Compact style={{ width: 440 }}>
                  <Select
                    aria-label="Search condition"
                    style={{ width: 160, flexShrink: 0 }}
                    options={URL_SEARCH_CONDITION_OPTIONS}
                    value={searchCondition}
                    onChange={handleConditionChange}
                    virtual={false}
                  />
                  <Input
                    suffix={<SearchOutlined />}
                    value={searchText}
                    onChange={handleSearchChange}
                    minLength={URL_SEARCH_MIN_LENGTH}
                    placeholder={URL_SEARCH_PLACEHOLDER}
                    allowClear
                  />
                </Space.Compact>
                <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                  Refresh
                </Button>
                <Button type="text" onClick={handleClearFilters}>
                  Clear filters
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  type="text"
                  onClick={() => setExportModalOpen(true)}
                >
                  Export
                </Button>
              </Flex>
              <Typography.Link
                href={TROUBLESHOOTING_GUIDE_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackEvent('Discoverability: Troubleshooting Guide Viewed', { tab: activeTab })
                }
                style={{ ...primaryLinkStyle, marginInlineEnd: token.marginXXS }}
              >
                <Flex gap={token.marginXXS} align="center">
                  <ArrowRightOutlined />
                  See troubleshooting guides
                </Flex>
              </Typography.Link>
            </Flex>

            {/* The table is always sorted (default ai_crawler_visits desc), so
            antd's darker "sort active" column shading would never turn off.
            The design keeps sorted columns looking like the others. */}
            <ConfigProvider
              theme={{
                components: {
                  Table: {
                    headerSortActiveBg: token.colorFillAlter,
                    bodySortBg: token.colorBgContainer,
                  },
                },
              }}
            >
              <Table<PageToReview>
                size="small"
                bordered
                columns={columns}
                dataSource={data?.items}
                // Keep the spinner up while correcting an out-of-range/junk page,
                // so the misleading healthy empty state never flashes before the
                // real rows load.
                loading={isFetching || isCorrectingPagination}
                // The empty states are designed without column headers.
                showHeader={Boolean(data?.items.length)}
                rowKey="url"
                onChange={handleTableChange}
                showSorterTooltip={false}
                pagination={{
                  current: requestedPage,
                  pageSize,
                  total: data?.totalCount,
                  onChange: (newPage, newPageSize) => {
                    if (newPageSize !== pageSize) updatePagination(1, newPageSize);
                    else updatePagination(newPage, newPageSize);
                  },
                  showSizeChanger: { showSearch: false },
                  pageSizeOptions: ['25', '50', '100'],
                }}
                locale={{
                  emptyText: tableParams.hasActiveFilters ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <Typography.Text style={{ fontSize: token.fontSize }}>
                          No pages match your filters
                        </Typography.Text>
                      }
                      style={{ padding: token.paddingXL }}
                    >
                      <Flex vertical gap={token.marginSM} align="center">
                        <Typography.Text
                          style={{
                            color: token.colorTextQuaternary,
                            fontSize: token.fontSizeSM,
                          }}
                        >
                          Try adjusting or clearing your search and filters.
                        </Typography.Text>
                        <Button onClick={handleClearFilters}>Clear Filters</Button>
                      </Flex>
                    </Empty>
                  ) : isCorrectingPagination ? null : (
                    <div style={{ padding: token.paddingXL }}>
                      <AiInsightsEmptyState headline={emptyState.headline} body={emptyState.body} />
                    </div>
                  ),
                }}
              />
            </ConfigProvider>
          </>
        )}
      </Card>

      {exportModalOpen && (
        <PagesToReviewExportModal
          open
          activeTab={activeTab}
          scope={pagesScope}
          filters={{
            sortBy: tableParams.sortBy,
            sortDirection: tableParams.sortDirection,
            urlSearch: tableParams.urlSearch,
            urlSearchCondition: tableParams.urlSearchCondition,
            statusEquals: tableParams.statusEquals,
            statusFrom: tableParams.statusFrom,
            statusTo: tableParams.statusTo,
          }}
          domain={domain}
          onClose={() => setExportModalOpen(false)}
        />
      )}
    </Flex>
  );
}
